import type {
    Setting, ActionSetting, BooleanSetting, EnumSetting, NumberSetting, StringSetting, ColorSetting,
    SettingsLayoutItem, SettingsLayoutColumns, SettingsLayoutGroup,
    SettingsPage, SettingsPageTemplate,
} from ".";
import { getEntries, type ValueWritable, valueWritable } from "..";
import { loadPage, savePage } from "./settings";
import { openLink } from "../navigation/hashLinking";

type NoInfer<T> = [T][T extends any ? 0 : never];

type CreateSetting<T = Setting> =  Omit<T, "id" | "default">;

export function createSettings<
    Id extends string,
    Settings extends { [key in Id]: CreateSetting }
>(settings: Settings, setDefaults = true): ValueWritable<{
    [Id in keyof Settings]: Settings[Id] & { id: Id, default: Settings[Id]["value"] }
}> & { save: () => void, load: () => void } {

    if (setDefaults) {
        getEntries(settings).forEach(([id, setting]) => {
            (setting as Setting).id = id;
            (setting as Setting).default = (setting as Setting).value;
        });
    }

    return valueWritable(settings) as any;
}

export function page<
    Id extends string,
    Settings extends { [key in Id]: Setting }
>(
    settings: ValueWritable<Settings> & { save: () => void, load: () => void },
    name: string,
    layout: SettingsLayoutItem<NoInfer<Id>>[],
    template?: SettingsPageTemplate
): SettingsPage<Settings> {

    const id = name.toLowerCase().replace(/\s/g, "-");

    const save = () => savePage(page as any);
    const load = () => loadPage(page as any);
    const open = (setting?: Id) => openLink(setting ? `settings/${id}/${setting}` : `settings/${id}`);

    settings.save = save;
    settings.load = load;

    const page = {
        name,
        id,
        settings,
        layout,
        template,

        save,
        load,

        open
    };

    return page as any;
}

export function createSettingsPage(pages: SettingsPage[], footer?: string) {
    const load = () => pages.forEach(loadPage);
    const save = () => pages.forEach(savePage);

    load();

    return {
        pages,
        load,
        save,
        footer,
    };
}
export type SettingsPages = ReturnType<typeof createSettingsPage>;

export function validate(settings: SettingsPages) {
    for (let page of settings.pages) {
        const ids = Object.values(page.settings.value).map(s => s.id);

        function layoutIds(layout: SettingsLayoutItem): SettingsLayoutItem[] {
            if (typeof layout === "string") return [layout];
            return layout.settings.flat().flatMap(layoutIds);
        }

        const lids = page.layout.map(layoutIds).flat();


        const missing = ids
            .filter(id => !lids.includes(id));

        const duplicate = lids
            .filter((id, i) => lids.indexOf(id) !== i)
            // .filter((id, i, a) => a.indexOf(id) === i);

        if (missing.length)  console.error(`Missing settings in layout:`, missing);
        if (duplicate.length) console.error(`Duplicate settings in layout:`, duplicate);
    }
}


/// Layout

export function group<T>(name: string, settings: SettingsLayoutGroup<T>["settings"]): SettingsLayoutGroup<T> {
    return { type: "group", name, settings };
}

export function columns<T>(settings: SettingsLayoutColumns<T>["settings"]): SettingsLayoutColumns<T> {
    return { type: "columns", settings };
}

export function columnsGroup<T>(name: string, settings: SettingsLayoutColumns<T>["settings"]): SettingsLayoutGroup<T> {
    return { type: "group", name, settings: [columns(settings)] };
}


/// Settings

type SettingText = { prefix?: string, suffix?: string };

export function action(name: string, action: () => void, text: SettingText = {}): CreateSetting<ActionSetting> {
    return { type: "action", name, value: undefined, action, ...text };
}

export function boolean(name: string, value: boolean, text: SettingText = {}): CreateSetting<BooleanSetting> {
    return { type: "boolean", name, value, suffix: name, ...text };
}

export function select<T>(name: string, value: T, options: {
    name: string,
    value: T
}[], text: SettingText = {}): CreateSetting<EnumSetting> {
    return { type: "enum", name, value, options, prefix: name, ...text };
}

export function number(name: string, value: number, min: number = 0, max: number = 1000, step: number = 1, text: SettingText = {}): CreateSetting<NumberSetting> {
    return { type: "number", name, value, min, max, step, prefix: name, ...text };
}

export function text(name: string, value: string, text: SettingText = {}): CreateSetting<StringSetting> {
    return { type: "string", name, value, prefix: name, ...text };
}

export function color(name: string, value: string, text: SettingText = {}): CreateSetting<ColorSetting> {
    return { type: "color", name, value, prefix: name, ...text };
}


/// Testing

const a = createSettings({
    "val.bool": boolean("yes", true),
    "val.num": number("number", 0, 0, 10, 1),
});
const b = createSettings({
    "test3": {
        name: "Test1",
        type: "boolean",
        value: true,
    },
});


const n = a.value["val.bool"].value;

const p = page(a, "page", [
    columnsGroup("Columns", [["val.bool", "val.bool"], ["val.bool"]]),
    group("Group", [
        "val.bool",
        "val.bool",
    ]),
    {
        type: "group",
        name: "General",
        settings: ["val.num", "val.num"],
    },
]);
p.settings.value["val.bool"].value = false;
